// T54: "Dokumenty" u pracownika eventowego — Kamilkowa i niekaralność: data WYDANIA + skan (T80),
// wysyłka do weryfikacji, historia zgłoszeń. Ważność = wydanie + N miesięcy z Ustawień.
import React, { useState, useEffect, useCallback } from 'react';
import { Badge, useToast } from '../components/UI';
import api from '../utils/api';

const LICENSE_CATEGORIES = ['AM', 'A1', 'A2', 'A', 'B', 'B+E', 'C', 'C+E', 'D', 'T']; // jak w profilu (utils/license.js)
const DOCS = [
  { type: 'KAMILKOWA', label: 'Ustawa Kamilkowa', has: 'hasKamilkowa', until: 'kamilkowaExpiryDate', issued: 'kamilkowaIssuedAt', hint: 'Zaświadczenie z Rejestru Sprawców Przestępstw na Tle Seksualnym — wymagane przy pracy z dziećmi.' },
  { type: 'NIEKARALNOSC', label: 'Zaświadczenie o niekaralności', has: 'hasNiekaralnosc', until: 'niekaralnoscExpiryDate', issued: 'niekaralnoscIssuedAt', hint: 'Z Krajowego Rejestru Karnego.' },
  // T140: data ważności z dokumentu (nie data wydania)
  { type: 'STUDENT_ID', label: 'Legitymacja szkolna / studencka', byValidUntil: true, hint: 'Potrzebna do umowy (uczniowie i studenci do 26 lat — bez składek ZUS na zleceniu). Zdjęcie strony z ważnością / hologramem.' },
  { type: 'DRIVING_LICENSE', label: 'Prawo jazdy', byValidUntil: true, noScan: true, hint: 'Bez zdjęcia — tylko data ważności (pole 4b) i kategorie. Oryginał miej przy sobie do wglądu.' }, // T141
];
const fmtD = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : null;

export default function MyDocumentsPage() {
  const [data, setData] = useState(null);
  const { show, ToastComponent } = useToast();
  const load = useCallback(() => api.getMyDocuments().then(setData).catch(e => show(e.message, 'error')), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p style={{ padding: 20, color: '#94A3B8' }}>Ładowanie...</p>;

  return (
    <div className="flex flex-col gap-14">
      <h2 className="page-title">Moje dokumenty</h2>
      <div className="card" style={{ fontSize: 12, color: '#475569' }}>
        Przy zaświadczeniach z KRK wpisz <b>datę wydania</b> (firma honoruje je przez określony czas), przy legitymacji i prawie jazdy —
        <b> datę ważności</b> z dokumentu. Dołącz zdjęcie lub PDF i wyślij do weryfikacji.
        Po zatwierdzeniu przez firmę dokument pojawi się w Twoim profilu i będziesz mógł być przypisywany do eventów,
        które go wymagają. Oryginał miej przy sobie do wglądu.
      </div>
      <div className="flex gap-8 flex-wrap" style={{ alignItems: 'stretch' }}>
        {DOCS.map(d => <DocCard key={d.type} doc={d} profile={data.profile} months={data.validityMonths?.[d.type]} submissions={data.submissions.filter(s => s.docType === d.type)} onChange={load} show={show} />)}
      </div>
      {ToastComponent}
    </div>
  );
}

const addMonths = (iso, n) => { const d = new Date(iso); d.setUTCMonth(d.getUTCMonth() + n); return d; };

function DocCard({ doc, profile, months, submissions, onChange, show }) {
  const lastOk = doc.byValidUntil ? submissions.find(s => s.status === 'APPROVED') : null; // T140
  const has = doc.byValidUntil ? !!lastOk : !!profile[doc.has];
  const until = doc.byValidUntil ? lastOk?.validUntil : profile[doc.until];
  const issuedProfile = doc.byValidUntil ? null : profile[doc.issued];
  const expired = until && new Date(until) < new Date();
  const pending = submissions.find(s => s.status === 'PENDING');
  const [form, setForm] = useState(false);
  const [issuedAt, setIssuedAt] = useState('');
  const [validUntil, setValidUntil] = useState(''); // T140
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  // T140: konto bez prawa jazdy / nie uczeń → pytamy o uzupełnienie przy zgłoszeniu
  const askLicense = doc.type === 'DRIVING_LICENSE'; // T141: kategorie zawsze (podpowiadamy te z konta)
  const licenseNow = String(profile.drivingLicense || '').split(',').map(x => x.trim()).filter(Boolean);
  const askStudent = doc.type === 'STUDENT_ID' && !['PUPIL', 'STUDENT'].includes(profile.studentStatus);
  const [cats, setCats] = useState(licenseNow);
  const [since, setSince] = useState('');
  const [student, setStudent] = useState('');

  // podgląd ważności: wydanie + N miesięcy z Ustawień (0 = bezterminowo)
  const todayIso = new Date().toISOString().slice(0, 10);
  const previewUntil = !doc.byValidUntil && issuedAt && months ? addMonths(issuedAt, months) : null;
  const tooOld = doc.byValidUntil ? (validUntil && validUntil < todayIso) : (previewUntil && previewUntil < new Date(todayIso));
  const ready = (doc.byValidUntil ? !!validUntil : !!issuedAt) && (doc.noScan || !!file) && !tooOld && (!askStudent || !!student) && (!askLicense || cats.length > 0);
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const attachment = doc.noScan ? undefined : await api.fileToBase64(file);
      await api.submitDocument({
        docType: doc.type, attachment,
        ...(doc.byValidUntil ? { validUntil } : { issuedAt }),
        ...(askLicense ? { drivingLicense: cats, licenseSince: since || undefined } : {}),
        ...(askStudent ? { studentStatus: student } : {}),
      });
      show(askLicense || askStudent ? 'Wysłane do weryfikacji — konto uzupełnione.' : 'Wysłane do weryfikacji.');
      setForm(false); setIssuedAt(''); setValidUntil(''); setFile(null); setSince(''); setStudent(''); onChange();
    } catch (e) { show(e.message, 'error'); }
    setBusy(false);
  };
  const pickFile = (e) => { const f = e.target.files?.[0] || null; const err = api.checkFile(f); if (err) { show(err, 'error'); e.target.value = ''; setFile(null); } else setFile(f); };

  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 13, fontWeight: 700 }}>🪪 {doc.label}</h3>
      <div style={{ fontSize: 10, color: '#64748B', marginBottom: 8 }}>{doc.hint}</div>

      <div style={{ fontSize: 12, padding: 8, borderRadius: 8, background: has && !expired ? '#ECFDF5' : '#FEF2F2', color: has && !expired ? '#065F46' : '#991B1B', marginBottom: 8 }}>
        {has && !expired && <>✓ {doc.byValidUntil ? 'Zatwierdzone' : 'W profilu'}: <b>mam</b>{issuedProfile ? `, wydane ${fmtD(issuedProfile)}` : ''}{until ? `, ${doc.byValidUntil ? 'ważne' : 'honorowane'} do ${fmtD(until)}` : ' (bezterminowo)'}</>}
        {has && expired && <>✗ W profilu: był, ale <b>wygasł {fmtD(until)}</b> — zgłoś nowy</>}
        {!has && <>✗ W profilu: <b>brak</b></>}
      </div>

      {pending ? (
        <div style={{ fontSize: 12, padding: 8, borderRadius: 8, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          ⏳ Zgłoszenie z {fmtD(pending.createdAt)} <b>czeka na weryfikację</b>{pending.issuedAt ? ` (wydane ${fmtD(pending.issuedAt)})` : ''}{pending.attachments?.length ? ', skan dołączony' : ''}.
        </div>
      ) : !form ? (
        <button className="btn btn-primary btn-sm" onClick={() => setForm(true)}>{has ? 'Zgłoś nowy / przedłużony' : 'Mam ten dokument — zgłoś'}</button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {doc.byValidUntil ? (
            <div className="input-group"><label>Ważny do <span style={{ color: '#DC2626' }}>*</span></label><input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} min={todayIso} />
              {tooOld && <div style={{ fontSize: 10, marginTop: 2, color: '#DC2626' }}>Ten dokument jest już nieważny — potrzebny aktualny.</div>}
            </div>
          ) : (
          <div className="input-group"><label>Data wydania <span style={{ color: '#DC2626' }}>*</span></label><input type="date" value={issuedAt} onChange={e => setIssuedAt(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
            {issuedAt && (
              <div style={{ fontSize: 10, marginTop: 2, color: tooOld ? '#DC2626' : '#059669' }}>
                {!months ? 'Honorowane bezterminowo.' : tooOld
                  ? `Za stare — honorujemy ${months} mies. od wydania (było ważne do ${previewUntil.toLocaleDateString('pl-PL')}). Potrzebne nowe zaświadczenie.`
                  : `Będzie honorowane do ${previewUntil.toLocaleDateString('pl-PL')} (${months} mies. od wydania).`}
              </div>
            )}
          </div>
          )}
          {askLicense && (
            <div style={{ fontSize: 11, padding: 8, borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <div style={{ marginBottom: 6 }}>Kategorie z prawa jazdy <span style={{ color: '#DC2626' }}>*</span> {licenseNow.length
                ? <span style={{ color: '#64748B' }}>(w koncie: {licenseNow.join(', ')} — popraw, jeśli się zmieniły)</span>
                : <span style={{ color: '#64748B' }}>(w koncie: <b>brak</b> — uzupełnimy)</span>}</div>
              <div className="flex gap-4 flex-wrap">
                {LICENSE_CATEGORIES.map(c => <button key={c} type="button" className={`filter-pill ${cats.includes(c) ? 'active' : ''}`} onClick={() => setCats(x => x.includes(c) ? x.filter(y => y !== c) : [...x, c])}>{c}</button>)}
              </div>
              {cats.length > 0 && <div className="input-group" style={{ maxWidth: 200, marginTop: 6 }}><label>Od roku (opcjonalnie)</label><input type="number" min="1960" max={new Date().getFullYear()} value={since} onChange={e => setSince(e.target.value)} placeholder="np. 2019" /></div>}

            </div>
          )}
          {askStudent && (
            <div style={{ fontSize: 11, padding: 8, borderRadius: 8, background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
              <div style={{ marginBottom: 6 }}>W Twoim koncie: <b>{profile.studentStatus === 'NONE' ? 'nie jestem uczniem ani studentem' : 'status nauki nie podany'}</b>. Zmienić na: <span style={{ color: '#DC2626' }}>*</span></div>
              <div className="flex gap-4">
                {[['PUPIL', 'Uczeń'], ['STUDENT', 'Student']].map(([v, l]) => <button key={v} type="button" className={`filter-pill ${student === v ? 'active' : ''}`} onClick={() => setStudent(v)}>{l}</button>)}
              </div>
            </div>
          )}
          {!doc.noScan && (
          <div className="input-group"><label>Skan / zdjęcie dokumentu <span style={{ color: '#DC2626' }}>*</span> <span style={{ fontWeight: 400, color: '#94A3B8' }}>(JPG, PNG lub PDF, do 5 MB)</span></label>
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" onChange={pickFile} />
            {file && <div style={{ fontSize: 10, color: '#059669', marginTop: 2 }}>✓ {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
          </div>
          )}
          {!ready && !tooOld && <div style={{ fontSize: 10, color: '#B45309' }}>{doc.noScan ? 'Podaj datę ważności i zaznacz kategorie.' : doc.byValidUntil ? 'Bez daty ważności i skanu nie da się wysłać.' : 'Bez daty wydania i skanu nie da się wysłać.'}{askStudent && !student ? ' Zaznacz: uczeń czy student.' : ''}</div>}
          <div className="flex gap-6 justify-end"><button className="btn btn-outline btn-sm" onClick={() => setForm(false)}>Anuluj</button><button className="btn btn-primary btn-sm" onClick={submit} disabled={busy || !ready}>{busy ? 'Wysyłanie…' : 'Wyślij do weryfikacji'}</button></div>
        </div>
      )}

      {submissions.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11 }}>
          <div style={{ fontWeight: 700, color: '#64748B', marginBottom: 4 }}>Historia</div>
          {submissions.map(s => (
            <div key={s.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '3px 0', borderTop: '1px solid #F1F5F9' }}>
              <span>{fmtD(s.createdAt)}</span>
              <Badge status={s.status} />
              <span style={{ color: '#64748B' }}>{s.issuedAt ? `wydane ${fmtD(s.issuedAt)}` : ''}{s.validUntil ? ` · do ${fmtD(s.validUntil)}` : ''}</span>
              {s.attachments?.map(a => <button key={a.id} className="btn btn-ghost btn-sm" style={{ padding: '0 4px' }} onClick={() => api.openAttachment(a.id).catch(e => show(e.message, 'error'))}>📎</button>)}
              {s.status === 'REJECTED' && s.note && <span style={{ color: '#DC2626' }}>— {s.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
